<template>
  <Teleport to="body">
    <van-overlay :show="visible" z-index="3000">
      <div class="img-preview" v-if="visible">
        <div class="img-preview-header"
          :style="{
            top: isMyOppo ? '20px' : '0'
          }"
        >
          <div class="img-preview-header_t">
            <svg-icon name="icon-close-bg" class="close" @click.stop="hidePreview" />
            <div class="page" v-if="imgList.length > 1">
              <slot name="pagetitle">
                <span>{{ showIndex + 1 }}/{{ imgList.length }} </span>
              </slot>
            </div>
          </div>
          <div
            class="img-preview-author"
            v-if="avatarInfo?.id"
          >
            <Avatar :avatarInfo="avatarInfo" :callBack="hidePreview">
              <span>{{ avatarInfo.userName }}</span>
            </Avatar>
            <Follow
              class="follow"
              :uid="authorInfo.id"
              :relation="relation"
              @change="onChangeFollowStatus"
            />
          </div>
          <div
            class="live-box"
            @click.stop="toPlayLive"
            v-show="imgList[showIndex]?.type === 5"
          >
            <svg-icon name="icon-live"></svg-icon>
            <p>{{ $t('liveName') }}</p>
          </div>
        </div>
        <div class="img-preview-container">
          <div ref="imgContainer">
            <img
              v-show="showImg && !showLiveVideo"
              ref="imgRef"
              :src="imgList[showIndex]?.bigSize"
              @load="imgLoaded"
            />
            <video
              v-show="showLiveVideo"
              id="live-video"
              muted
              :src="imgList[showIndex]?.microVideo"
            ></video>
          </div>
          <svg-icon
            v-show="loading"
            name="icon-media-loading"
            class="loading"
          />
        </div>
        <div class="img-preview-foot-container">
          <ObButton
            v-if="
              !imgList[showIndex]?.loadedOrigin && !imgList[showIndex]?.type
            "
            class="view-Origin"
            :border="false"
            backgroundColor="rgba(0, 0, 0, 0.54)"
            fontColor="#FFF"
            @click.stop="viewOrigin"
          >
            {{ $t('gallery.viewOrigin') }}
          </ObButton>
          <ObButton
            v-if="imgList[showIndex]?.type === 5 && imgList[showIndex]?.origin"
            class="view-Origin"
            :border="false"
            backgroundColor="rgba(0, 0, 0, 0.54)"
            fontColor="#FFF"
            @click.stop="downOrigin(imgList[showIndex]?.origin)"
          >
            {{ $t('gallery.downOrigin') }}
          </ObButton>
          <div class="img-preview-foot">
            <template v-if="authorInfo?.id">
              <ObButton
                v-if="showThreadEntry"
                class="view-detail"
                :text="$t('gallery.viewDetails')"
                :border="false"
                backgroundColor="#FFF"
                fontColor="rgba(0, 0, 0, .95)"
                @click.stop="toThread"
              />
              <ObButton
                v-if="showDownloadBtn"
                class="download-image"
                fontColor="#FFF"
                @click.stop="downOrigin(imgList[showIndex]?.origin)"
              >
                <svg-icon name="icon-download" />
                <span>{{ $t('gallery.download') }}</span>
              </ObButton>
              <ObButton
                :class="'like-btn ' + (praised ? 'like' : 'unlike')"
                :border="false"
                @click.stop="likeRef.onClickIcon()"
              >
                <Like
                  ref="likeRef"
                  :uid="authorInfo.id"
                  :id="tid"
                  :count="praiseCount"
                  :status="praised"
                  @update:status="onChangeLikeStatus"
                />
                <span class="like-count">
                  {{ $numFormat(praiseCount) + ' ' + $t('like', praiseCount) }}
                </span>
              </ObButton>
            </template>
          </div>
        </div>
      </div>
    </van-overlay>
  </Teleport>
</template>

<script setup>
import Hammer from 'hammerjs';

import { $debounce } from '@/utils/lodashFuncs';
import { storeToRefs } from 'pinia';
import { useSystemStore } from '@/stores/system';
import { blobToBase64 } from '@/utils';

const route = useRoute();
const router = useRouter();

const { proxy } = getCurrentInstance();

const systemStore = useSystemStore();
const { isApp, isOneplus, isOppoApp, isMyOppo, versionCode } = storeToRefs(systemStore);

const likeRef = ref(null);
const emit = defineEmits(['update:visible', 'update:previewIndex', 'changeFollow', 'changeLike', 'next']);
const imgList = ref([]);
const showIndex = ref(0);
const loading = ref(false);
const showImg = ref(false);
// 图片缩放

const imgContainer = ref(null);
const imgRef = ref();

const threshold = ref(100);
const lastHammerEventTime = ref(0);

let pinchScale = 1;
let position = ref({ x: 0, y: 0 });

// 关注状态
const relation = ref(0);
const avatarInfo = ref(null);

const showLiveVideo = ref(false);

const props = defineProps({
  previewList: {
    type: Array,
    default: []
  },
  visible: {
    type: Boolean,
    default: false
  },
  previewIndex: {
    type: Number,
    default: 0
  },
  authorInfo: {
    type: Object,
    default: null
  },
  tid: {
    type: [Number, String],
    default: null
  },
  praiseCount: {
    type: Number,
    default: 0
  },
  praised: {
    type: Boolean,
    default: false
  },
  showThreadEntry: {
    type: Boolean,
    default: true
  },
  showDownloadBtn: {
    type: Boolean,
    default: false
  },
});

const imgTochAction = () => {
  const hammer = new Hammer(imgContainer.value);
  hammer.get('pinch').set({ enable: true });
  hammer.get('pan').set({ direction: Hammer.DIRECTION_ALL });
  // hammer.get('swipe').set({ direction: Hammer.DIRECTION_ALL });

  hammer.on('pinchstart pinchmove', (event) => {
    event.preventDefault();

    const scale = Math.min(Math.max(pinchScale * event.scale, 1), 2);

    if (imgRef.value) {
      const { x, y } = position.value;
      imgRef.value.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${scale})`;
    }
  });

  hammer.on('pinchend pinchcancel', (event) => {
    if (event.type === 'pinchend') {
      pinchScale = Math.min(Math.max(pinchScale * event.scale, 1), 2);
    }
  });

  hammer.on('panstart panmove', (event) => {
    event.preventDefault();

    // 当图片缩放时才允许拖拽
    if (pinchScale === 1) return;
    const { x, y } = position.value;

    position.value = {
      x: event.deltaX,
      y: event.deltaY
    };

    if (imgRef.value) {
      const { x, y } = position.value;
      imgRef.value.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${pinchScale})`;
    }
  });

  hammer.on('panend pancancel', (event) => {
    if (event.type === 'panend') {
      position.value.x = event.deltaX;
      position.value.y = event.deltaY;
    }
  });
  //监听双击事件
  hammer.on('doubletap', () => {
    resetImg();
  });
  hammer.on(
    'swiperight swipeleft ',
    $debounce(({ type, timeStamp }) => {
      if (timeStamp - lastHammerEventTime.value < 500 || pinchScale !== 1)
        return;
      lastHammerEventTime.value = timeStamp;
      switch (type) {
        case 'swiperight':
          if (showIndex.value > 0) {
            // showIndex.value--;
            switchImg(
              showIndex.value - 1,
              imgList.value[showIndex.value - 1]?.type
            );
          }
          break;
        case 'swipeleft':
          // 限制在边界内，如果已是最后一张图片，则不再向后切换
          if (showIndex.value < imgList.value.length - 1) {
            // showIndex.value += 1;
            switchImg(
              showIndex.value + 1,
              imgList.value[showIndex.value + 1]?.type
            );
            emit('next', showIndex.value + 1)
          }
          break;
        default:
      }
    })
  );
};

const resetImg = () => {
  //重置图片缩放大小
  pinchScale = 1;
  //重置图片位置
  position.value = { x: 0, y: 0 };
  //修改图片样式
  if (imgRef.value) {
    imgRef.value.style.transform = `translate3d(0px, 0px, 0) scale(1)`;
  }
};

const hidePreview = () => {
  emit('update:visible', false);
};

const mediaType = ref(-1);
const switchImg = (index, type) => {
  if (index === showIndex.value) return;

  mediaType.value = type;

  if (index < 0) index += imgList.value.length;

  if (index >= imgList.value.length) index -= imgList.value.length;

  showImg.value = false;
  loading.value = true;

  showIndex.value = index;
  emit('update:previewIndex', showIndex.value)
  resetImg();
};

const toThread = () => {
  hidePreview();
  if (isApp.value) {
    // APP内通过jsAPI调用
    proxy.$openNewWebview(`${location.origin}/thread/${props.tid}`);
  } else {
    router.push({
      name: 'thread',
      params: {
        id: props.tid
      }
    });
  }
};

const imgLoaded = (e) => {
  showImg.value = true;
  loading.value = false;

  // 如果是实况图 先播放视频 隐藏封面图
  showLiveVideo.value = false;
  if (mediaType.value === 5) {
    toPlayLive();
  }

  let showItem = imgList.value[showIndex.value];
  // 加载的是原图，把查看原图按钮隐藏
  if (showItem.bigSize === showItem.origin) {
    imgList.value[showIndex.value].loadedOrigin = true;
  }
  imgTochAction();
};

// 查看原图
const viewOrigin = () => {
  loading.value = true;
  imgList.value[showIndex.value].bigSize =
    imgList.value[showIndex.value].origin;
};

// 下载原图
const downloading = ref(false);
const downOrigin = async (link) => {
  proxy.$ga('download', {
    "entry_position": 'Image Preview'
  })
  if (isApp.value) {
    if (isOneplus.value && versionCode.value < 42600) {
      proxy.$toast({ message: proxy.$t('appNotShare'), duration: 4000 });
      return;
    }
    proxy.$transferData({
      imgurl: link
    });
  } else {
    if (downloading.value) return;
    downloading.value = true;
    const img = new Image();
    img.src = link;
    img.setAttribute('crossorigin', 'anonymous');
    img.onload = async function () {
      const blobRes = await fetch(link).then((res) => res.blob());
      const base64 = await blobToBase64(blobRes);
      const url = document.createElement('a');
      url.download = 'download';
      url.href = base64;
      url.click();
      downloading.value = false;
    };
  }
};

// 关注/取消关注 后同步更新关注状态
const onChangeFollowStatus = (e) => {
  relation.value = e.relation;
  emit('changeFollow', e.relation);
};

// 点赞/取消点赞 状态变更
const onChangeLikeStatus = (val) => {
  emit('changeLike', val);
};

const toPlayLive = () => {
  if (showLiveVideo.value) return;
  showLiveVideo.value = true;
  const videoElement = document.getElementById('live-video');
  videoElement
    .play()
    .then(() => {
      // console.log(`autoplay well`);
    })
    .catch((e) => {
      // console.log(`autoplay fail, wait for first click`);
    });
  videoElement.onended = () => {
    showLiveVideo.value = false;
  };
};

watch(
  () => [props.visible, props.previewList],
  ([inputVisible, previewList]) => {
    if (inputVisible) {
      if ((isOneplus.value || isOppoApp.value) && route.query.hideNavigation) {
        proxy.$handleHomeHorizontalScroll();
      }
      loading.value = true;
      // 隐藏滚动条
      showIndex.value = null; // 这里设为null，保证展示 loading 状态
      avatarInfo.value = { ...props.authorInfo };
      relation.value = props.authorInfo?.relation;
      imgList.value = previewList.map((e) => {
        // 实况图
        if (e?.type && e?.type === 5) {
          return {
            type: e.type,
            thumbnail: proxy.$formatImg(e.coverImage, 80), // 侧边栏缩略图
            bigSize: proxy.$formatImg(e.coverImage, 1000, '', false), // 压缩大图
            origin: e.url, // 原图
            microVideo: e.microVideoTranscodeInfo?.[0].url || e.microVideo // 视频动图
          };
        }
        // 普通图
        if(e?.includes('?x-ocs-process=')){
          e = e.split('?x-ocs-process=')[0]
        }
        return {
          thumbnail: proxy.$formatImg(e, 80), // 侧边栏缩略图
          bigSize: proxy.$formatImg(e, 1000, '', false), // 压缩大图
          origin: e, // 原图
          loadedOrigin: false // 是否加载过原图
        };
      });
      console.log(props.previewIndex)
      if (showIndex.value === props.previewIndex) return
      switchImg(props.previewIndex, imgList.value[props.previewIndex]?.type);
    } else {
      avatarInfo.value = null;
      imgList.value = [];
      document.body.style.overflow = 'auto';
      if ((isOneplus.value || isOppoApp.value) && route.query.hideNavigation) {
        proxy.$handleHomeHorizontalScroll(1);
      }
    }
  }, {
    immediate: true,
    deep: true
  }
);

</script>

<style lang="postcss" scoped>
.img-preview {
  background-color: #101010;
  height: calc(100vh - var(--browser-address-bar, 0px));
}

.img-preview-header {
  position: absolute;
  top: 0;
  display: flex;
  flex-flow: column nowrap;
  width: 100vw;
  padding: 16px 20px 44px;
  box-sizing: border-box;
  background: linear-gradient(180deg, rgba(16, 16, 16, 0.85) -3.48%, rgba(16, 16, 16, 0.35) 59.71%, rgba(34, 34, 34, 0.00) 99.56%);
  z-index: 999;
  .img-preview-header_t {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
    height: 28px;
  }
  .svg-icon {
    color: #303030;
  }
  .page {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: flex-start;
    color: #fff;
    font-size: 12px;
  }
  .live-box {
    display: flex;
    justify-content: center;
    align-items: center;
    width: fit-content;
    z-index: 99;
    background-color: rgba(255, 255, 255, 0.15);
    font-size: 14px;
    color: #fff;
    padding: 2px 4px;
    border-radius: 2px;
    cursor: pointer;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    .icon-live {
      width: 16px;
      height: 16px;
      margin-right: 2px;
    }
    p {
      height: 20px;
      line-height: 20px !important;
    }
  }
}
.img-preview-author {
  display: flex;
  align-items: center;
  justify-content: space-between;
  .avatar {
    color: #fff;
    :deep(.avatar-image) {
      margin-right: 8px;
    }
    span {
      max-width: 170px;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  .follow {
    font-size: 12px;
    background-color: transparent;
    height: 28px !important;
    padding: 6px 27px;
    border: 1px solid rgba(255, 255, 255, 0.55);
    /* 覆盖外部的icon按钮尺寸 */
    --icon-button-size: 22px !important;

    :deep(span) {
      /* height:18px; */
      display: block;
    }

    :deep(.svg-icon) {
      height: 18px;
    }
  }
}

.img-preview-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100vw;
  height: 100%;
  overflow: auto;
  box-sizing: border-box;
  img {
    max-width: 100vw;
    height: calc(100vh - var(--browser-address-bar, 0px));
    object-fit: contain;
    background-color: #101010;
  }
  .loading {
    /* 固定在屏幕最中间 */
    position: absolute;
    top: calc(100% / 2 - 12);
    left: 50%;
    margin-left: -12px;
    height: 24px;
    width: 24px;
    color: white;
    animation: rotate 1s linear infinite;
  }
  #live-video {
    max-width: 100%;
    max-height: 100%;
  }
  @keyframes rotate {
    0% {
      transform: rotate(0);
    }
    100% {
      transform: rotate(360deg);
    }
  }
}

.img-preview-foot-container {
  position: absolute;
  bottom: 0;
  display: flex;
  flex-flow: column wrap;
  align-items: center;
  background: linear-gradient(0, #101010 -2.16%, rgba(16, 16, 16, 0.60) 52.92%, rgba(34, 34, 34, 0.00) 98.24%);
}
.view-Origin {
  width: fit-content;
  height: 28px;
  padding: 5px 9px;
  font-size: 11px;
  margin-top: 6px;
  border: 0.6px solid rgba(255, 255, 255, 0.3);
  background-color: rgba(0, 0, 0, 0.3);
  .icon-download {
    color: #fff;
  }
}
.img-preview-foot {
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  width: 100vw;
  padding: 20px 20px 42px;
  box-sizing: border-box;
  a {
    display: inline-block;
    .ob-button {
      width: 100%;
    }
  }
  :deep(.ob-button) {
    opacity: 1;
    span {
      font-size: 14px;
      line-height: 22px !important;
    }
  }
  .download-image {
    background-color: transparent;
    border: 1px solid #FFF;
  }
  .like-btn {
    &:active {
      background-color: var(--theme);
    }
    display: flex;
    align-items: center;
    margin-right: 10px;
    padding: 0 20px;
    margin-left: 16px;
    color: #fff;
    .icon-unlike {
      width: 20px;
      height: 20px;
      margin-right: 4px;
      color: #fff;
    }
    .like-count {
      font-size: 14px;
    }
  }

  .like {
    &:active {
      background-color: rgba(213, 213, 213, 1);
    }
    background-color: rgba(213, 213, 213, 1);
    border-color: rgba(213, 213, 213, 1);
    color: var(--text-primary);
  }
}
</style>
